import { Injectable } from '@angular/core';
import gql from 'graphql-tag';
import { AuthenticationService } from '../authentication/authentication.service';
import { GraphqlService } from '../http/graphql.service';
import { LocaleService } from '../locale/locale.service';

@Injectable({ providedIn: 'root' })
export class UserService {

  private createUserMutation = gql`
    mutation($data: UserCreateInput!) {
      createUser(data: $data) {
        id
      }
    }`;

  private updateUserMutation = gql`
    mutation($data: UserUpdateInput!, $where: UserWhereUniqueInput!) {
      updateUser(data: $data, where: $where) {
        id
      }
    }
  `;

  private emailByEmailQuery = gql`query($email: String) {
      user(where: { email: $email }) {
        email
      }
    }`;

  private requestPasswordResetMutation = gql`
    mutation($email: String!) {
      requestPasswordReset(email: $email) {
        id
      }
    }
  `;

  private changePasswordMutation = gql`
    mutation($passwordResetId: String!, $newPassword: String!) {
      changePassword(
        passwordResetId: $passwordResetId
        newPassword: $newPassword
      ) {
        message
      }
    }
  `;

  private limitedUserStartSessionDataQuery = gql`
    query($id: ID!) {
      user(where: { id: $id }) {
        id
        places {
          id
          isUsingAnotherStock
          localServingStock {
            id
          }
          stock {
            description {
              id
              name
              localizationKey
              unit
              type
              chemicalPurity
              category
              utility
            }
            amount
          }
          id
          localName
          address {
            id
            latitude
            longitude
          }
          pools(where: { isDisabled: false }) {
            id
            name
            volume
            isEnabledAutomaticSuggestion
            hasTabletApplicationSupport
          }
        }
        subscriptionEnd
        standardAppSubscriptionEnd
      }
    }
  `;

  private startSessionDataQuery = gql`
    query($id: ID!) {
      user(where: { id: $id }) {
        id
        employee {
          clockIns(last: 1) {
            id
            uuid
            start
            startInCell
            ends
            endsInCell
          }
          visits(last: 1) {
            id
            uuid
            start
            startInCell
            ends
            endsInCell
            local {
              id
              isUsingAnotherStock
              localServingStock {
                id
              }
              stock {
                description {
                  id
                  name
                  localizationKey
                  unit
                  type
                  chemicalPurity
                  category
                  utility
                }
                amount
              }
              id
              localName
              address {
                id
                latitude
                longitude
              }
              pools {
                id
                name
                volume
                isEnabledAutomaticSuggestion
                hasTabletApplicationSupport
              }
            }
          }
        }
      }
    }
  `;

  private deleteUserMutation = gql`
    mutation($id: ID!) {
      deleteUser(where: { id: $id }) {
        id
      }
    }`;

  private disconectLocalMutation = gql`
    mutation ($id: ID!, $localId: ID!) {
      updateUser(where: {id: $id}, data: {
        places: { disconnect: {id: $localId} }
      }) {
        id
      }
    }
  `;

  constructor(
    private graphqlService: GraphqlService,
    private authService: AuthenticationService,
    private localeService: LocaleService,
  ) { }

  public async deleteUser(id) {
    await this.graphqlService.mutate(this.deleteUserMutation, { id });
    this.authService.logout('Usuário deletado com sucesso');
  }

  public async saveUser(newUser, selectedLocals, localDefault?, phoneList?) {
    const localToConnect = selectedLocals.map((local) => ({ id: local.id }));
    const variables = {
      franchisee: newUser.franchisee
        ? { connect: { email: newUser.franchisee } }
        : null,
      email: newUser.email,
      password: newUser.password,
      cpfCnpj: newUser.cpfCnpj ? newUser.cpfCnpj.replace(/\.|\-|\//g, '') : null,
      roles: { set: newUser.role },
      places: { connect: localToConnect },
      telephones: { create: phoneList || [] },
      localDefault: localDefault ? { connect: { id: localDefault.id } } : null,
      standardAppSubscriptionToken: newUser.standardAppSubscriptionToken || null,
      standardAppSubscriptionId: newUser.standardAppSubscriptionId || null,
      locale: newUser.locale || this.localeService.getCurrentLanguage(),
      ...this.getEmployeeClient(newUser),
    };
    await this.graphqlService.mutate(this.createUserMutation, { data: variables });
  }

  public async updateUser(data, where) {
    await this.graphqlService.mutate(this.updateUserMutation, { data, where });
  }

  public requestPasswordReset(email) {
    return this.graphqlService.mutate(this.requestPasswordResetMutation, { email });
  }

  public changePassword(passwordResetId: string, newPassword: string) {
    return this.graphqlService
      .mutate(this.changePasswordMutation, { passwordResetId, newPassword }, false)
      .then((response: any) => response.changePassword);
  }

  public getEmailInUseInfo(email) {
    return this.graphqlService.query(this.emailByEmailQuery, { email })
      .then((resp: any) => Boolean(resp.user));
  }

  public getStartSessionData(id) {
    return this.graphqlService.query(this.startSessionDataQuery, { id });
  }

  public getLimitedUserStartSessionData(id) {
    return this.graphqlService.query(this.limitedUserStartSessionDataQuery, { id });
  }

  private getEmployeeClient(newUser) {
    const data = { create: { name: newUser.name } };
    return newUser.role === 'CLIENT' ? { client: data } : { employee: data };
  }

  public async disconnectLocal(id, localId) {
    return this.graphqlService.mutate(this.disconectLocalMutation, { id, localId });
  }

}
